import React, { useEffect, useMemo } from 'react'
import './index.scss'
import { stateAbbreviations } from '../validateUsaState'
import { BLACK } from 'constants/colors'
import Icon from 'component_library/1_Atoms/Icon'
import { providerTypes, stateLocationPageUrls } from '../constants'
import useBreakpoint from 'hooks/useBreakPoint'
import { popularCities } from './constants'
import { formatCity } from '../utils'
import { enhanceUrlWithMarketingParams } from 'lib/marketingParams'

type HubStatesProps = {
  validStates: Record<string, string[]>
  type: string
}

const HubStates: React.FC<HubStatesProps> = ({ validStates, type }) => {
  const { isMobileScreen, isLargeSize } = useBreakpoint()

  const [statesColumns, setStateColumns] = React.useState([])
  const [cityColumns, setCityColumns] = React.useState([])
  const [showStatesList, setShowStatesList] = React.useState<boolean>(true)
  const [selectedState, setSelectedState] = React.useState<string>(null)

  // Function to organize items into 4 columns
  const getColumns = (items, numColumns) => {
    const columns = Array.from({ length: numColumns }, () => [])
    const rowsNeeded = Math.ceil(Object.keys(items).length / numColumns)
    items.forEach((item, index) => {
      columns[Math.floor(index / rowsNeeded)].push(item)
    })
    return columns
  }

  const formattedPopularCities = useMemo(() => {
    const cities =
      type === providerTypes.PSYCHIATRISTS
        ? popularCities.psychiatrists
        : popularCities.therapists

    const validStatesLower = Object.keys(validStates).map((state) =>
      state.toLowerCase()
    )

    // Filter and transform the cities object based on valid state codes
    const filteredPopularCities = Object.entries(cities)
      .filter(([key]) => validStatesLower.includes(key))
      .flatMap(([state, citiesArr]) =>
        citiesArr.map((city) => [
          `${city}, ${state.toUpperCase()}`,
          `/${type}/${state}/${formatCity(city)}`,
        ])
      )

    return filteredPopularCities
  }, [type])

  useEffect(() => {
    const statesSorted = Object.keys(
      validStates
    ).sort((stateCode1, stateCode2) =>
      stateAbbreviations[stateCode1].localeCompare(
        stateAbbreviations[stateCode2]
      )
    )
    const numColumns = isLargeSize ? 3 : isMobileScreen ? 2 : 4

    setStateColumns(getColumns(statesSorted, numColumns))
    setCityColumns(getColumns(formattedPopularCities, numColumns))
  }, [validStates, formattedPopularCities, isLargeSize, isMobileScreen])

  const showStatesListHandler = () => {
    setSelectedState(null)
    setShowStatesList(true)
  }

  const stateCardClickHandler = (state) => {
    setSelectedState(state)
    setShowStatesList(false)
  }

  const stateListHeaderText =
    type === providerTypes.PSYCHIATRISTS
      ? 'States With Psychiatric Availability'
      : 'States With Availability'

  return (
    <div className="HubStates">
      {formattedPopularCities.length > 0 && (
        <div className="HubStates__popCityList">
          <h3 className="HubStates__popCityList__title">Most Popular Cities</h3>

          <div className="HubStates__popCityList__subContainer">
            {cityColumns.map((column, colIndex) => (
              <div key={colIndex} className="HubStates__popCityList__column">
                {column.map(([cityText, cityUrl], cityIndex) => (
                  <div
                    key={cityIndex}
                    className="HubStates__popCityList__cityName"
                  >
                    <a href={enhanceUrlWithMarketingParams(cityUrl)}>
                      {cityText}
                    </a>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {showStatesList ? (
        <div className="HubStates__stateList">
          <h3 className="HubStates__stateList__title">{stateListHeaderText}</h3>

          <div className="HubStates__stateList__subContainer">
            {statesColumns.map((statesColumn, columnIndex) => (
              <div
                className="HubStates__stateList__column"
                key={`sefriendly-hub-${statesColumn}-${columnIndex}`}
              >
                {statesColumn.map((state) => (
                  <div
                    className="HubStates__stateList__stateCard"
                    key={`sefriendly-hub-${state}`}
                    onClick={() => stateCardClickHandler(state)}
                  >
                    <div className="HubStates__stateList__stateCard__stateName">
                      {stateAbbreviations[state]}
                    </div>
                    <div className="HubStates__stateList__stateCard__backArrow">
                      <Icon icon="iosBackRight" fill={BLACK} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="HubStates__allCities">
          <span
            onClick={showStatesListHandler}
            className="HubStates__allCities__backLink"
          >
            <Icon icon="leftArrow" />
            Back to state list
          </span>
          <span className="HubStates__allCities__seeAllProviderLink">
            <a
              href={enhanceUrlWithMarketingParams(
                stateLocationPageUrls[selectedState?.toLowerCase()] ||
                  `/${type}/${selectedState?.toLowerCase()}`
              )}
            >
              See all providers in{' '}
              <strong>{stateAbbreviations[selectedState]}</strong>
            </a>
          </span>
          <div className="HubStates__allCities__list">
            {validStates[selectedState].map((city, index) => (
              <div key={index} className="HubStates__allCities__cityName">
                <a
                  key={`sefriendly-hub-state-${city}`}
                  href={enhanceUrlWithMarketingParams(
                    `/${type}/${selectedState.toLowerCase()}/${formatCity(
                      city
                    )}`
                  )}
                >
                  {city}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default HubStates
